import type { AppWavePin } from '@keboola/api-client/developerPortal/types';
import { Button, Modal, setSubmitError, toast, useAppForm } from '@keboola/design';

import { SaveButton } from '../../../components/SaveButton';
import { selectSaveButtonState } from '../../../components/saveButtonState';
import { getApiErrorMessage } from '../../auth/errors';
import { useSetWavePins } from '../queries';
import {
  describeWave,
  ignoredPins,
  pinsFromSelection,
  readPins,
  shownWaves,
  toWaveTags,
} from '../waves';

const FORM_ID = 'edit-wave-pins';

type Props = {
  appId: string;
  /** The `waveTags` the dialog opened with; Save refuses when the live pins differ. */
  openedWith: unknown;
  candidates: readonly AppWavePin[];
  onSaved: () => void;
};

/**
 * One select per wave, and Save.
 *
 * - A pin apps-api ignores (no candidate has its version and tag) starts as "No pin".
 * - Save is offered while such a pin exists, so it can be cleared without another edit.
 * - Save sends the whole pin set; no pins sends `null`, which clears them.
 */
export function WavePinsFields({ appId, openedWith, candidates, onSaved }: Props) {
  const setPins = useSetWavePins(appId);
  const current = readPins(openedWith);
  const ignored = ignoredPins(current, candidates);
  const waves = shownWaves(current);

  const items = [
    { value: '', label: 'No pin' },
    ...candidates.map((pin) => ({
      value: String(pin.version),
      label: `${pin.tag} (version ${pin.version})`,
    })),
  ];

  const defaultPins: Record<string, string> = Object.fromEntries(
    waves.map((wave) => [
      String(wave),
      ignored.has(wave) ? '' : String(current.get(wave)?.version ?? ''),
    ]),
  );

  const form = useAppForm({
    defaultValues: { pins: defaultPins },
    onSubmit: async ({ value, formApi }) => {
      try {
        await setPins.mutateAsync({
          from: openedWith,
          to: toWaveTags(pinsFromSelection(value.pins, candidates)),
        });
        toast.success('The wave pins were saved.');
        onSaved();
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'Could not save the wave pins.'));
      }
    },
  });

  const helpFor = (draft: ReturnType<typeof readPins>, wave: number) => {
    const runs = `Runs: ${describeWave(draft, wave)}`;
    const skipped = ignored.get(wave);
    return skipped === undefined
      ? runs
      : `${runs}. The current pin, ${skipped.tag} (version ${skipped.version}), is ignored by apps-api: it no longer matches the current image.`;
  };

  return (
    <>
      <Modal.Body>
        <form.AppForm>
          <form.FormBody id={FORM_ID}>
            <form.Subscribe selector={(state) => state.values.pins}>
              {(selection) => {
                const draft = pinsFromSelection(selection, candidates);
                return waves.map((wave) => (
                  <form.AppField key={wave} name={`pins.${wave}`}>
                    {(field) => (
                      <field.SelectField
                        label={`Wave ${wave}`}
                        items={items}
                        help={helpFor(draft, wave)}
                      />
                    )}
                  </form.AppField>
                ));
              }}
            </form.Subscribe>
            <form.SubmitErrorAlert />
          </form.FormBody>
        </form.AppForm>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Close asChild>
          <Button type="button" variant="secondary">
            Cancel
          </Button>
        </Modal.Close>
        <form.Subscribe
          selector={(state) => ({
            ...selectSaveButtonState(state),
            isUnchanged: state.isDefaultValue && ignored.size === 0,
          })}
        >
          {(save) => (
            <SaveButton state={save} form={FORM_ID}>
              Save pins
            </SaveButton>
          )}
        </form.Subscribe>
      </Modal.Footer>
    </>
  );
}
